/**
 * On-screen debug logger for environments where DevTools is unavailable
 * (e.g. Telegram Mini App on mobile).
 *
 * Enabled only when window.__ENV__.DEBUG === 'true'.
 * Renders a semi-transparent overlay in the bottom half of the screen.
 * Tapping the overlay copies all log lines to the clipboard.
 */

const isDebug = () => window.__ENV__?.DEBUG === 'true';

const MAX_LINES = 60;
const lines = [];

function getOrCreatePanel() {
    let panel = document.getElementById('__debug_panel__');
    if (panel) return panel;

    panel = document.createElement('div');
    panel.id = '__debug_panel__';
    Object.assign(panel.style, {
        position:     'fixed',
        bottom:       '0',
        left:         '0',
        right:        '0',
        maxHeight:    '40vh',
        overflowY:    'auto',
        background:   'rgba(0,0,0,0.82)',
        color:        '#00ff99',
        fontFamily:   'monospace',
        fontSize:     '11px',
        lineHeight:   '1.4',
        padding:      '6px 8px',
        zIndex:       '99999',
        whiteSpace:   'pre-wrap',
        wordBreak:    'break-all',
        boxSizing:    'border-box',
        borderTop:    '1px solid #00ff99',
        cursor:       'pointer',
        userSelect:   'none',
    });

    panel.title = 'Tap to copy logs';
    panel.addEventListener('click', () => {
        const text = lines.join('\n');
        navigator.clipboard?.writeText(text).catch(() => {});
    });

    document.body.appendChild(panel);
    return panel;
}

function renderLine(text) {
    const panel = getOrCreatePanel();
    panel.textContent = lines.join('\n');
    panel.scrollTop = panel.scrollHeight;
    // Re-render efficiently: replace full text to avoid DOM bloat
    void text; // already pushed to lines[] before this call
    panel.textContent = lines.join('\n');
    panel.scrollTop = panel.scrollHeight;
}

function format(level, args) {
    const time = new Date().toTimeString().slice(0, 8);
    const msg = args.map(a => {
        if (a === null) return 'null';
        if (a === undefined) return 'undefined';
        if (a instanceof Error) return `${a.name}: ${a.message}`;
        if (typeof a === 'object') {
            try { return JSON.stringify(a); } catch { return String(a); }
        }
        return String(a);
    }).join(' ');
    return `[${time}] ${level} ${msg}`;
}

export function log(...args) {
    if (!isDebug()) return;
    const line = format('LOG', args);
    lines.push(line);
    if (lines.length > MAX_LINES) lines.shift();
    if (document.body) {
        renderLine(line);
    } else {
        document.addEventListener('DOMContentLoaded', () => renderLine(line), { once: true });
    }
}

export function warn(...args) {
    if (!isDebug()) return;
    const line = format('WRN', args);
    lines.push(line);
    if (lines.length > MAX_LINES) lines.shift();
    if (document.body) renderLine(line);
}

export function error(...args) {
    if (!isDebug()) return;
    const line = format('ERR', args);
    lines.push(line);
    if (lines.length > MAX_LINES) lines.shift();
    if (document.body) renderLine(line);
}
